import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { cn } from '../lib/utils';

// Links to in-page anchors on the home route
const homeLinks = [
  { hash: '#experiences', label: 'Experiences' },
  { hash: '#offerings', label: 'Fishing' },
  { hash: '#rates', label: 'Rates' },
  { hash: '#ecology', label: 'Ecology' },
  { hash: '#gallery', label: 'Gallery' },
  { hash: '#story', label: 'Story' },
  { hash: '#faq', label: 'FAQ' },
  { hash: '#contact', label: 'Contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Smooth scroll when clicking same-page hash
  const onHashClick = (e, hash) => {
    if (onHome) {
      e.preventDefault();
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        'fixed top-0 inset-x-0 z-50 transition-all duration-500',
        scrolled || !onHome
          ? 'bg-sand/90 backdrop-blur-xl border-b border-moss/20'
          : 'bg-transparent'
      )}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 flex items-center justify-between h-16 md:h-20 gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-baseline gap-2 group min-w-0">
          <span className="font-display text-xl md:text-2xl text-pond tracking-tight leading-none">
            Asana
          </span>
          <span className="overline text-muted hidden md:inline truncate">
            Fishing &amp; Ecological Resorts
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6 2xl:gap-7">
          {homeLinks.map((l) => (
            <a
              key={l.hash}
              href={onHome ? l.hash : `/${l.hash}`}
              onClick={(e) => onHashClick(e, l.hash)}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm text-charcoal/80 hover:text-pond transition-colors font-medium tracking-wide whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/getting-here"
            data-testid="nav-getting-here"
            className={cn(
              'text-sm font-medium tracking-wide whitespace-nowrap transition-colors',
              location.pathname === '/getting-here'
                ? 'text-pond underline underline-offset-4 decoration-moss'
                : 'text-charcoal/80 hover:text-pond'
            )}
          >
            Getting Here
          </Link>
        </nav>

        <Link
          to="/#book"
          data-testid="nav-book-cta"
          className="hidden md:inline-flex items-center justify-center gap-2 rounded-full font-sans font-medium tracking-wide transition-all duration-300 bg-pond text-offwhite hover:bg-moss hover:text-pond hover:-translate-y-0.5 shadow-md px-5 py-2.5 text-sm whitespace-nowrap"
        >
          Book a Visit
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden text-pond p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-sand/95 backdrop-blur-xl border-t border-moss/20"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {homeLinks.map((l) => (
              <a
                key={l.hash}
                href={onHome ? l.hash : `/${l.hash}`}
                onClick={(e) => onHashClick(e, l.hash)}
                className="text-charcoal/85 hover:text-pond text-base font-medium"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/getting-here"
              onClick={() => setOpen(false)}
              data-testid="mobile-getting-here"
              className="text-charcoal/85 hover:text-pond text-base font-medium"
            >
              Getting Here
            </Link>
            <Link
              to="/#book"
              onClick={() => setOpen(false)}
              data-testid="mobile-book-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full font-medium bg-pond text-offwhite px-6 py-3 text-sm mt-2"
            >
              Book a Visit
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
